import {
  signOut as firebaseSignOut,
  GithubAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  type User as FirebaseUser,
} from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { auth, githubProvider, googleProvider } from '@/services/firebase';

type Provider = 'google' | 'github';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthUser {
  uid: string;
  name: string;
  email: string;
  provider: Provider;
  avatarUrl: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  signIn: (provider: Provider) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function resolveProvider(user: FirebaseUser): Provider {
  const providerId = user.providerData[0]?.providerId ?? '';
  if (providerId.includes('github')) return 'github';
  return 'google';
}

function toAuthUser(user: FirebaseUser): AuthUser {
  return {
    uid: user.uid,
    name: user.displayName ?? user.email ?? 'Unknown user',
    email: user.email ?? 'unknown@mtgcraft.dev',
    provider: resolveProvider(user),
    avatarUrl: user.photoURL ?? 'https://www.gravatar.com/avatar/?d=identicon',
  };
}

function providerFor(provider: Provider): GoogleAuthProvider | GithubAuthProvider {
  return provider === 'google' ? googleProvider : githubProvider;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(toAuthUser(firebaseUser));
        setStatus('authenticated');
        return;
      }

      setUser(null);
      setStatus('unauthenticated');
    });
    return unsubscribe;
  }, []);

  const signIn = useCallback(async (provider: Provider) => {
    await signInWithPopup(auth, providerFor(provider));
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ user, status, signIn, signOut }), [user, status, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
